import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, RefreshCw, Mail, ExternalLink, Filter, Inbox,
  CheckCircle2, Clock, AlertCircle, Archive,
} from "lucide-react";

/**
 * Admin Ticket Viewer — Update #174
 *
 * Full support-inbox for the tickets filed via the public /contact
 * page. Lives inside the authenticated dashboard so only admin users
 * (role="admin" or "super_admin") can see it. Uses the shared
 * app-shell layout (rendered from App.js Outlet), so we only render
 * the page body here — no header / sidebar.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status metadata ─────────────────────────────────────────────────
const STATUS_META = {
  new:         { label: "New",         color: "text-blue-400",    bg: "bg-blue-500/10 border-blue-500/30",       icon: AlertCircle },
  in_progress: { label: "In progress", color: "text-amber-400",   bg: "bg-amber-500/10 border-amber-500/30",     icon: Clock       },
  resolved:    { label: "Resolved",    color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2},
  closed:      { label: "Closed",      color: "text-slate-400",   bg: "bg-slate-500/10 border-slate-500/30",     icon: Archive     },
};

// ── Auth helper ─────────────────────────────────────────────────────
function _auth() {
  const token =
    localStorage.getItem("session_token") ||
    document.cookie.split("session_token=")[1]?.split(";")[0];
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminTickets() {
  const [tickets,       setTickets]       = useState([]);
  const [counts,        setCounts]        = useState({});
  const [total,         setTotal]         = useState(0);
  const [statusFilter,  setStatusFilter]  = useState("");
  const [topicFilter,   setTopicFilter]   = useState("");
  const [search,        setSearch]        = useState("");
  const [topics,        setTopics]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [selectedId,    setSelectedId]    = useState(null);
  const [selectedRow,   setSelectedRow]   = useState(null);
  const [rowLoading,    setRowLoading]    = useState(false);

  // Fetch topics on mount (for the filter dropdown)
  useEffect(() => {
    fetch(`${API}/support/tickets/topics`)
      .then((r) => r.json())
      .then((d) => setTopics(d.topics || []))
      .catch(() => {});
  }, []);

  // Fetch tickets whenever filters change (debounced search)
  useEffect(() => {
    const t = setTimeout(() => loadTickets(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [statusFilter, topicFilter, search]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (topicFilter)  params.set("topic",  topicFilter);
      if (search)       params.set("search", search);
      const r = await fetch(`${API}/support/tickets?${params.toString()}`, {
        headers: _auth(),
      });
      if (r.status === 401 || r.status === 403) {
        toast.error("Admin access required to view tickets.");
        setTickets([]);
        return;
      }
      const d = await r.json();
      setTickets(d.tickets || []);
      setCounts(d.counts  || {});
      setTotal(d.total    || 0);
    } catch (e) {
      toast.error("Could not load tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (id) => {
    setSelectedId(id);
    setRowLoading(true);
    try {
      const r = await fetch(`${API}/support/tickets/${id}`, { headers: _auth() });
      if (!r.ok) throw new Error("failed");
      const d = await r.json();
      setSelectedRow(d.ticket);
    } catch {
      toast.error("Could not load ticket details.");
      setSelectedId(null);
    } finally {
      setRowLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const r = await fetch(`${API}/support/tickets/${id}`, {
        method: "PATCH",
        headers: { ..._auth(), "Content-Type": "application/json" },
        body:    JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      // Optimistically patch the row in the list + the open modal
      setTickets((prev) => prev.map((t) => (t.ticket_id === id ? d.ticket : t)));
      if (selectedRow?.ticket_id === id) setSelectedRow(d.ticket);
      toast.success(`Ticket marked as ${STATUS_META[status]?.label || status}`);
      // Refresh counts
      loadTickets();
    } catch {
      toast.error("Could not update ticket. Please try again.");
    }
  };

  const closeModal = () => { setSelectedId(null); setSelectedRow(null); };

  const filtered = tickets;

  return (
    <div className="min-h-screen bg-bg-base text-slate-100 px-6 py-8" data-testid="admin-tickets-page">
      <PageHeader counts={counts} total={total} onRefresh={loadTickets} loading={loading} />

      <Toolbar
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        topicFilter={topicFilter}   setTopicFilter={setTopicFilter}
        search={search}             setSearch={setSearch}
        topics={topics}
      />

      <TicketTable
        tickets={filtered}
        loading={loading}
        onRowClick={openTicket}
      />

      {selectedId && (
        <TicketModal
          ticket={selectedRow}
          loading={rowLoading}
          onClose={closeModal}
          onStatusChange={(status) => updateStatus(selectedId, status)}
        />
      )}
    </div>
  );
}

// ── Header with status counts ──────────────────────────────────────
const PageHeader = ({ counts, total, onRefresh, loading }) => (
  <div className="flex items-start justify-between mb-6 gap-6 flex-wrap"
       data-testid="admin-tickets-header">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-white flex items-center gap-3">
        <Inbox className="h-6 w-6 text-blue-400" />
        Support tickets
      </h1>
      <p className="text-sm text-slate-400 mt-1">
        {total} total ticket{total === 1 ? "" : "s"} filed via the Contact page.
      </p>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      {["new", "in_progress", "resolved", "closed"].map((s) => {
        const meta = STATUS_META[s];
        const Ico  = meta.icon;
        return (
          <div key={s} className={`px-3 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-2 ${meta.bg}`}
               data-testid={`count-${s}`}>
            <Ico className={`h-3.5 w-3.5 ${meta.color}`} />
            <span className={meta.color}>{counts[s] || 0}</span>
            <span className="text-slate-400">{meta.label}</span>
          </div>
        );
      })}
      <button
        type="button"
        onClick={onRefresh}
        data-testid="admin-tickets-refresh"
        disabled={loading}
        className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 text-xs font-medium inline-flex items-center gap-2 transition disabled:opacity-50"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  </div>
);

// ── Filter toolbar ─────────────────────────────────────────────────
const Toolbar = ({ statusFilter, setStatusFilter, topicFilter, setTopicFilter, search, setSearch, topics }) => (
  <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-slate-800"
       data-testid="admin-tickets-toolbar">
    <div className="relative flex-1 min-w-[240px] max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input
        type="text"
        placeholder="Search name, station, email, message, or ticket ID…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="admin-tickets-search"
        className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
    </div>
    <div className="flex items-center gap-2">
      <Filter className="h-4 w-4 text-slate-500" />
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        data-testid="admin-tickets-status-filter"
        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
      >
        <option value="">All statuses</option>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <option key={k} value={k}>{v.label}</option>
        ))}
      </select>
      <select
        value={topicFilter}
        onChange={(e) => setTopicFilter(e.target.value)}
        data-testid="admin-tickets-topic-filter"
        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
      >
        <option value="">All topics</option>
        {topics.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
    </div>
  </div>
);

// ── Ticket table ───────────────────────────────────────────────────
const TicketTable = ({ tickets, loading, onRowClick }) => {
  if (loading && tickets.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-16 text-center text-slate-500"
           data-testid="admin-tickets-loading">
        Loading tickets…
      </div>
    );
  }
  if (tickets.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-16 text-center"
           data-testid="admin-tickets-empty">
        <Inbox className="h-12 w-12 text-slate-600 mx-auto mb-3" />
        <div className="text-slate-300 font-medium">No tickets match those filters</div>
        <div className="text-xs text-slate-500 mt-1">
          Try adjusting the search or status filter.
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="admin-tickets-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-3 font-medium">Status</th>
            <th className="text-left px-4 py-3 font-medium">Submitter</th>
            <th className="text-left px-4 py-3 font-medium">Station</th>
            <th className="text-left px-4 py-3 font-medium">Topic</th>
            <th className="text-left px-4 py-3 font-medium">Preview</th>
            <th className="text-left px-4 py-3 font-medium">Filed</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.new;
            const Ico  = meta.icon;
            return (
              <tr
                key={t.ticket_id}
                onClick={() => onRowClick(t.ticket_id)}
                data-testid={`ticket-row-${t.ticket_id}`}
                className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer transition"
              >
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.bg}`}>
                    <Ico className={`h-3 w-3 ${meta.color}`} />
                    <span className={meta.color}>{meta.label}</span>
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.email}</div>
                </td>
                <td className="px-4 py-3 text-slate-300">{t.station_name}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{t.topic_label}</td>
                <td className="px-4 py-3 text-slate-400 max-w-[280px] truncate">
                  {t.message}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                  {relativeTime(t.submitted_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Detail modal ───────────────────────────────────────────────────
const TicketModal = ({ ticket, loading, onClose, onStatusChange }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mailtoHref = useMemo(() => {
    if (!ticket) return "#";
    const subject = encodeURIComponent(`Re: [${ticket.ticket_id}] Your BroadcastFlow AI support ticket`);
    const body    = encodeURIComponent(
      `Hi ${ticket.name},\n\n\n\n` +
      `--- Your original message ---\n` +
      `Topic: ${ticket.topic_label}\n` +
      `Station: ${ticket.station_name}\n\n` +
      `${ticket.message}\n\n` +
      `Reference: ${ticket.ticket_id}\n`
    );
    return `mailto:${ticket.email}?subject=${subject}&body=${body}`;
  }, [ticket]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
         onClick={onClose}
         data-testid="ticket-modal">
      <div
        className="w-full max-w-2xl bg-slate-950 border border-slate-800 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !ticket ? (
          <div className="p-16 text-center text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-blue-400" data-testid="modal-ticket-id">
                  {ticket.ticket_id}
                </span>
                <StatusBadge status={ticket.status} />
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="ticket-modal-close"
                className="text-slate-500 hover:text-white text-2xl leading-none"
              >×</button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-white">{ticket.name}</h2>
                <div className="text-sm text-slate-400 mt-0.5">
                  {ticket.station_name} · <a href={`mailto:${ticket.email}`} className="text-blue-400 hover:underline">{ticket.email}</a>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Filed {absoluteTime(ticket.submitted_at)} · {ticket.topic_label}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Message</div>
                <div className="bg-slate-900 border-l-2 border-blue-500 rounded-r-lg p-4 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed"
                     data-testid="ticket-modal-message">
                  {ticket.message}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <MetaRow label="Email confirmation">
                  {ticket.email_confirmation?.sent
                    ? <span className="text-emerald-400">Delivered · {ticket.email_confirmation?.resend_id?.slice(-8) || "ok"}</span>
                    : <span className="text-amber-400">Not delivered{ticket.email_confirmation?.error ? ` · ${String(ticket.email_confirmation.error).slice(0, 60)}` : ""}</span>}
                </MetaRow>
                <MetaRow label="Assignee">
                  {ticket.assignee || <span className="text-slate-500">Unassigned</span>}
                </MetaRow>
                {ticket.updated_at && (
                  <MetaRow label="Last updated">
                    {absoluteTime(ticket.updated_at)}{ticket.updated_by ? ` · ${ticket.updated_by}` : ""}
                  </MetaRow>
                )}
                {ticket.ip_hint && (
                  <MetaRow label="Source IP">
                    <span className="font-mono">{ticket.ip_hint}</span>
                  </MetaRow>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-800 flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_META)
                  .filter(([k]) => k !== ticket.status)
                  .map(([k, v]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => onStatusChange(k)}
                      data-testid={`ticket-set-status-${k}`}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium hover:brightness-125 transition ${v.bg} ${v.color}`}
                    >
                      Mark as {v.label}
                    </button>
                  ))}
              </div>
              <a
                href={mailtoHref}
                data-testid="ticket-reply-mailto"
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium inline-flex items-center gap-2 transition"
              >
                <Mail className="h-4 w-4" />
                Reply via email
                <ExternalLink className="h-3 w-3 opacity-70" />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Small subcomponents ────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Ico  = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.bg}`}>
      <Ico className={`h-3 w-3 ${meta.color}`} />
      <span className={meta.color}>{meta.label}</span>
    </span>
  );
};

const MetaRow = ({ label, children }) => (
  <div>
    <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">{label}</div>
    <div className="text-slate-300">{children}</div>
  </div>
);

// ── Time helpers ───────────────────────────────────────────────────
function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now  = Date.now();
  const s    = Math.floor((now - then) / 1000);
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function absoluteTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium", timeStyle: "short",
  });
}
